// Watchlist-focused route: a full-width, sortable view of every watched security.
//
// Complements the dashboard's compact watchlist card. The dashboard keeps a dense summary
// alongside the account overview; this page is the place to scan and sort the full set.
// Reuses the same read-only watchlist API (list/add/remove) and the shared formatters.

import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  ArrowLeftRight,
  ChevronDown,
  ChevronRight,
  Plus,
  RefreshCw,
  Star,
  Trash2,
  X,
} from "lucide-react";

import { ApiClientError } from "../api/client";
import { useApi } from "../api/useApi";
import { STARTER_SYMBOLS } from "../lib/starterSymbols";
import { useVisiblePolling } from "../lib/useVisiblePolling";
import {
  dailyPriceChange,
  quoteSessionLine,
  quoteTime,
  signedMoney,
} from "../lib/quoteFormat";
import type { WatchlistItem } from "../types";
import {
  cagr,
  changeColor,
  money,
  shortName,
  signedPercent,
  sinceSpan,
  timeAgo,
} from "../lib/format";
import { type Quality, qualityColor, qualityLabel, rateMetric } from "../lib/metricQuality";
import {
  DEFAULT_RISK_WINDOW,
  RISK_WINDOWS,
  type RiskWindowKey,
  hasWindow,
  isRiskWindowKey,
  riskForWindow,
} from "../lib/riskWindows";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// Sortable columns. Each maps to a comparable numeric (or string) accessor on a row.
type SortKey =
  | "symbol"
  | "latest_price"
  | "today_change_percent"
  | "drawdown_percent"
  | "ytd"
  | "one_year"
  | "three_year"
  | "five_year"
  | "ten_year";

type SortDir = "asc" | "desc";

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: "symbol", label: "Symbol", numeric: false },
  { key: "latest_price", label: "Last price", numeric: true },
  { key: "today_change_percent", label: "Change", numeric: true },
  { key: "drawdown_percent", label: "Drawdown", numeric: true },
  { key: "ytd", label: "YTD", numeric: true },
  { key: "one_year", label: "1Y", numeric: true },
  { key: "three_year", label: "3Y CAGR", numeric: true },
  { key: "five_year", label: "5Y CAGR", numeric: true },
  { key: "ten_year", label: "10Y CAGR", numeric: true },
];

// Client-side mirror of the server's refresh cooldown (backend REFRESH_COOLDOWN_SECONDS).
// After a successful refresh the button is disabled for this long so a user does not keep
// triggering the (provider-costly) recompute. The server stays authoritative; this is UX.
const REFRESH_COOLDOWN_MS = 600_000;

/** Seconds to wait from a refresh 429, or null if the error is not a cooldown response. */
function refreshCooldownRetrySeconds(err: unknown): number | null {
  if (
    err instanceof ApiClientError &&
    err.status === 429 &&
    typeof err.body === "object" &&
    err.body !== null
  ) {
    const body = err.body as { code?: unknown; retry_after_seconds?: unknown };
    if (body.code === "refresh_cooldown" && typeof body.retry_after_seconds === "number") {
      return body.retry_after_seconds;
    }
  }
  return null;
}

/** A friendly "Xm Ys" / "Ys" label for a countdown. */
function countdownLabel(seconds: number): string {
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  return `${seconds}s`;
}

/** A unitless ratio to 2 decimals, or an em dash when absent. */
function ratio(value: number | undefined | null): string {
  if (value === undefined || value === null) return "—";
  return value.toFixed(2);
}

/** One labeled risk figure in the expandable detail. ``quality`` color-codes the value
    good / ok / bad when the metric has a clear direction. */
function RiskStat({
  label,
  value,
  hint,
  quality,
}: {
  label: string;
  value: string;
  hint?: string;
  quality?: Quality | null;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={"tabular-nums " + qualityColor(quality ?? null)}
        title={quality ? `${label}: ${qualityLabel(quality)}` : undefined}
      >
        {value}
      </div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

/** Price vs a moving average: a short trend read ("above" / "below"), colored like a
    gain/loss. Returns an em dash when either value is missing. */
function MovingAverageStat({
  label,
  price,
  ma,
}: {
  label: string;
  price: number | undefined | null;
  ma: number | undefined | null;
}) {
  if (ma === undefined || ma === null) {
    return <RiskStat label={label} value="—" hint="not enough history" />;
  }
  const hasPrice = price !== undefined && price !== null;
  const above = hasPrice && price >= ma;
  const hint = hasPrice ? (above ? "price above" : "price below") : undefined;
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="tabular-nums">{money(ma)}</div>
      {hint && (
        <div className={"text-[10px] " + (above ? "text-success" : "text-destructive")}>
          {hint}
        </div>
      )}
    </div>
  );
}

/** A small window selector (1Y / 3Y / 5Y / 10Y / Max). Windows without computed metrics
    for this security are disabled rather than hidden, so the available history is clear. */
function WindowTabs({
  windowed,
  selected,
  onSelect,
}: {
  windowed: WatchlistItem["risk"];
  selected: RiskWindowKey;
  onSelect: (key: RiskWindowKey) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1" role="tablist" aria-label="risk window">
      {RISK_WINDOWS.map((w) => {
        const available = hasWindow(riskForWindow(windowed, w.key));
        const active = w.key === selected;
        return (
          <button
            key={w.key}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={!available}
            aria-label={`risk-window-${w.key}`}
            onClick={() => onSelect(w.key)}
            className={
              "rounded px-2 py-0.5 text-xs font-medium transition-colors " +
              (active
                ? "bg-primary/15 text-foreground"
                : available
                  ? "text-muted-foreground hover:text-foreground"
                  : "cursor-not-allowed text-muted-foreground/40")
            }
          >
            {w.label}
          </button>
        );
      })}
    </div>
  );
}

/** Per-symbol risk metrics (window-selectable) plus the 50/200-day moving averages, shown
    when a watchlist row is expanded. All windows are captured server-side; the selector
    switches which one is shown without any new request. */
function RiskDetail({
  item,
  preferredWindow,
  onSelectWindow,
}: {
  item: WatchlistItem;
  preferredWindow: RiskWindowKey;
  onSelectWindow: (key: RiskWindowKey) => void;
}) {
  // Use the saved preference when this security supports it. Young securities fall back
  // locally without overwriting the user's global preference.
  const windowKey = hasWindow(riskForWindow(item.risk, preferredWindow))
    ? preferredWindow
    : RISK_WINDOWS.find((w) => hasWindow(riskForWindow(item.risk, w.key)))?.key
      ?? DEFAULT_RISK_WINDOW;

  const r = riskForWindow(item.risk, windowKey);
  const hasAnyRisk = RISK_WINDOWS.some((w) => hasWindow(riskForWindow(item.risk, w.key)));
  const hasMovingAverages = item.ma_50 != null || item.ma_200 != null;

  if (!hasAnyRisk && !hasMovingAverages) {
    return (
      <p className="py-2 text-xs text-muted-foreground">
        Not enough price history yet to compute metrics for {item.symbol}.
      </p>
    );
  }

  const windowLabel = RISK_WINDOWS.find((w) => w.key === windowKey)?.label ?? "1Y";

  return (
    <div className="space-y-3 py-2">
      {hasAnyRisk && (
        <div className="space-y-1">
          <WindowTabs
            windowed={item.risk}
            selected={windowKey}
            onSelect={onSelectWindow}
          />
          {hasWindow(r) && r ? (
            <>
              <div className="grid grid-cols-3 gap-x-6 gap-y-2 sm:grid-cols-6">
                <RiskStat
                  label="Volatility"
                  value={ratio(r.volatility) + "%"}
                  hint="annualized"
                  quality={rateMetric("volatility", r.volatility)}
                />
                <RiskStat
                  label="Max drawdown"
                  value={ratio(r.max_drawdown) + "%"}
                  quality={rateMetric("max_drawdown", r.max_drawdown)}
                />
                <RiskStat label="Sharpe" value={ratio(r.sharpe)} quality={rateMetric("sharpe", r.sharpe)} />
                <RiskStat
                  label="Sortino"
                  value={ratio(r.sortino)}
                  quality={rateMetric("sortino", r.sortino)}
                />
                <RiskStat label="Calmar" value={ratio(r.calmar)} quality={rateMetric("calmar", r.calmar)} />
                <RiskStat label="Beta (vs SPY)" value={ratio(r.beta)} />
              </div>
              <p className="text-[10px] text-muted-foreground">
                {windowLabel === "Max"
                  ? `Full available history${
                      sinceSpan(r.start_date) ? ` (${sinceSpan(r.start_date)})` : ""
                    }`
                  : `Trailing ${windowLabel}`}
                , from daily total returns. Risk-free rate 4.5%.
              </p>
            </>
          ) : (
            <p className="py-1 text-xs text-muted-foreground">
              Not enough history for the {windowLabel} window.
            </p>
          )}
        </div>
      )}
      {hasMovingAverages && (
        <div className="space-y-1">
          <div className="grid grid-cols-3 gap-x-6 gap-y-2 sm:grid-cols-6">
            <MovingAverageStat label="50-day MA" price={item.latest_price} ma={item.ma_50} />
            <MovingAverageStat label="200-day MA" price={item.latest_price} ma={item.ma_200} />
          </div>
          <p className="text-[10px] text-muted-foreground">
            Simple moving averages of the daily close.
          </p>
        </div>
      )}
    </div>
  );
}

/** Compact "LABEL value" chip for the mobile cards, matching the dashboard watchlist. */
function ReturnTag({ label, value }: { label: string; value: number | undefined | null }) {
  // Stack the label over the value so a wide figure (e.g. +336.01%) never pushes into the
  // neighboring column. min-w-0 keeps each chip clipped to its grid cell.
  return (
    <span className="flex min-w-0 flex-col leading-tight">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={"tabular-nums " + changeColor(value)}>{signedPercent(value)}</span>
    </span>
  );
}

function QuoteRowsSkeleton({ rows = 5 }: { rows?: number }) {
  const items = Array.from({ length: rows });
  return (
    <div data-testid="watchlist-loading" aria-busy="true">
      <ul className="divide-y divide-border md:hidden">
        {items.map((_, index) => (
          <li key={index} className="space-y-2 py-2.5">
            <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3">
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-3 w-28 max-w-full" />
              </div>
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-3 w-12" />
              </div>
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-x-4 gap-y-1.5">
              {Array.from({ length: 6 }).map((_, metricIndex) => (
                <Skeleton key={metricIndex} className="h-3 w-12" />
              ))}
            </div>
            <div className="flex justify-end gap-0.5">
              <Skeleton className="size-7" />
              <Skeleton className="size-7" />
            </div>
          </li>
        ))}
      </ul>
      <div className="hidden space-y-2 py-2 md:block">
        {items.map((_, index) => (
          <div key={index} className="flex items-center gap-3">
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-3 w-28" />
            </div>
            <div className="ml-auto flex items-center gap-3">
              {Array.from({ length: 8 }).map((_, metricIndex) => (
                <Skeleton key={metricIndex} className="h-8 w-14" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function sortValue(item: WatchlistItem, key: SortKey): number | string | null | undefined {
  switch (key) {
    case "symbol":
      return item.symbol;
    case "latest_price":
      return item.latest_price;
    case "today_change_percent":
      return item.today_change_percent;
    case "drawdown_percent":
      return item.drawdown_percent;
    case "ytd":
      return item.returns?.ytd;
    case "one_year":
      return item.returns?.one_year;
    case "three_year":
      return cagr(item.returns?.three_year, 3);
    case "five_year":
      return cagr(item.returns?.five_year, 5);
    case "ten_year":
      return cagr(item.returns?.ten_year, 10);
  }
}

export function Watchlist() {
  const api = useApi();
  const navigate = useNavigate();
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [trackedSymbol, setTrackedSymbol] = useState<string | null>(null);
  // First symbol chosen for a two-security compare, or null. Picking a second navigates
  // to the Compare page with both.
  const [compareFrom, setCompareFrom] = useState<string | null>(null);
  const [newSymbol, setNewSymbol] = useState("");
  const [busy, setBusy] = useState(false);
  // Set while seeding the suggested starter symbols so the empty-state buttons disable and
  // show progress. Holds the symbol currently being added, or "all" for the add-all run.
  const [seeding, setSeeding] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("symbol");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [riskWindow, setRiskWindow] = useState<RiskWindowKey>(DEFAULT_RISK_WINDOW);
  // Symbols whose risk detail is expanded. Multiple rows can be open at once.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const isExpanded = (symbol: string) => expanded.has(symbol);
  const toggleExpanded = (symbol: string) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(symbol)) next.delete(symbol);
      else next.add(symbol);
      return next;
    });
  // Epoch ms until which the refresh button is on cooldown (mirrors the server's limit),
  // or null when refresh is available. Drives both the disabled state and the countdown.
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  // Ticks once a second while a cooldown is active so the countdown label updates.
  const [, setClock] = useState(0);

  const loadWatchlist = useCallback(async (background = false) => {
    if (!background) setRefreshing(true);
    try {
      setWatchlist(await api.listWatchlist());
    } catch (err) {
      if (!background) {
        setStatus(err instanceof Error ? err.message : "Failed to load watchlist");
      }
    } finally {
      if (!background) setRefreshing(false);
    }
  }, [api]);

  useVisiblePolling(() => loadWatchlist(true));

  // Recompute metrics server-side, then show the refreshed rows. A 429 means the server's
  // cooldown is still active; read its retry_after_seconds to disable the button locally.
  const refresh = useCallback(async () => {
    setRefreshing(true);
    setStatus("");
    try {
      setWatchlist(await api.refreshWatchlist());
      setCooldownUntil(Date.now() + REFRESH_COOLDOWN_MS);
    } catch (err) {
      const retry = refreshCooldownRetrySeconds(err);
      if (retry != null) {
        setCooldownUntil(Date.now() + retry * 1000);
      } else {
        setStatus(err instanceof Error ? err.message : "Failed to refresh watchlist");
      }
    } finally {
      setRefreshing(false);
    }
  }, [api]);

  // While a cooldown is pending, re-render each second so the remaining time counts down,
  // and clear it once elapsed.
  useEffect(() => {
    if (cooldownUntil == null) return;
    const id = setInterval(() => {
      if (Date.now() >= cooldownUntil) {
        setCooldownUntil(null);
      } else {
        setClock((c) => c + 1);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  const cooldownRemaining =
    cooldownUntil == null ? 0 : Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));

  const load = useCallback(async () => {
    // Target metadata and rows are independent requests; resolve them together so the
    // watchlist is not delayed by an extra API round trip.
    const targetRequest = api
      .listMarketTargets()
      .then((targets) => setTrackedSymbol(targets[0]?.symbol?.toUpperCase() ?? null))
      .catch(() => setTrackedSymbol(null));
    const preferenceRequest = api
      .getUserPreferences()
      .then((preferences) => {
        if (isRiskWindowKey(preferences.risk_window)) setRiskWindow(preferences.risk_window);
      })
      .catch(() => undefined);
    await Promise.all([targetRequest, preferenceRequest, loadWatchlist()]);
    setInitialLoad(false);
  }, [api, loadWatchlist]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectRiskWindow = useCallback((next: RiskWindowKey) => {
    setRiskWindow(next);
    void api.updateUserPreferences({ risk_window: next }).catch(() => {
      setStatus("The risk window changed for this session but could not be saved.");
    });
  }, [api]);

  const addSymbol = async () => {
    const symbol = newSymbol.trim().toUpperCase();
    if (!symbol) return;
    setBusy(true);
    setStatus("");
    try {
      await api.addWatchlist(symbol);
      setNewSymbol("");
      await loadWatchlist();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not add symbol");
    } finally {
      setBusy(false);
    }
  };

  const removeSymbol = async (symbol: string) => {
    setStatus("");
    try {
      await api.removeWatchlist(symbol);
      await loadWatchlist();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Could not remove symbol");
    }
  };

  // Add one suggested starter symbol from the empty state. Reuses the validated add path,
  // so an unrecognized ticker surfaces the normal error rather than silently failing.
  const addStarter = async (symbol: string) => {
    setSeeding(symbol);
    setStatus("");
    try {
      await api.addWatchlist(symbol);
      await loadWatchlist();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : `Could not add ${symbol}`);
    } finally {
      setSeeding(null);
    }
  };

  // Add every starter symbol in sequence (sequential, not parallel, to stay well within
  // the provider's rate limits). Any that fail are reported; the rest still land.
  const addAllStarters = async () => {
    setSeeding("all");
    setStatus("");
    const failed: string[] = [];
    for (const { symbol } of STARTER_SYMBOLS) {
      try {
        await api.addWatchlist(symbol);
      } catch {
        failed.push(symbol);
      }
    }
    await loadWatchlist();
    setSeeding(null);
    if (failed.length > 0) {
      setStatus(`Added the rest. Could not add: ${failed.join(", ")}.`);
    }
  };

  const isTracked = (symbol: string) => symbol.toUpperCase() === trackedSymbol;

  // Compare flow: the first "compare" click arms a selection; the second (on a different
  // symbol) navigates to the Compare page with both. Clicking the armed symbol clears it.
  const pickCompare = (symbol: string) => {
    if (compareFrom === null) {
      setCompareFrom(symbol);
      return;
    }
    if (compareFrom === symbol) {
      setCompareFrom(null);
      return;
    }
    const a = encodeURIComponent(compareFrom);
    const b = encodeURIComponent(symbol);
    navigate(`/compare?a=${a}&b=${b}`);
  };

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Numeric columns default to descending (biggest first is the usual intent); the
      // symbol column defaults to ascending (A-Z).
      setSortDir(key === "symbol" ? "asc" : "desc");
    }
  };

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const rows = [...watchlist].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      // Missing values always sort to the bottom, regardless of direction.
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string" || typeof bv === "string") {
        return dir * String(av).localeCompare(String(bv));
      }
      return dir * (av - bv);
    });
    // Starred (tracked) security always pins to the top, matching the dashboard.
    return rows.sort((a, b) => (isTracked(a.symbol) ? 0 : 1) - (isTracked(b.symbol) ? 0 : 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist, sortKey, sortDir, trackedSymbol]);

  const latestFetch = watchlist
    .map((w) => w.price_fetched_at)
    .filter(Boolean)
    .sort()
    .at(-1);

  // Match the dashboard's quote context: call out prices captured outside the regular
  // session without adding noise while the market is open.
  const priced = watchlist.filter((w) => w.latest_price != null);
  const sessionTag: "After hours" | "Market closed" | null = priced.some(
    (w) => w.price_session === "closed",
  )
    ? "Market closed"
    : priced.some((w) => w.price_session === "extended")
      ? "After hours"
      : null;

  const SortHeader = ({ col }: { col: (typeof COLUMNS)[number] }) => (
    <TableHead className={"h-8" + (col.numeric ? " text-right" : "")}>
      <button
        type="button"
        className={
          "inline-flex items-center gap-1 hover:text-foreground " +
          (col.numeric ? "flex-row-reverse" : "")
        }
        onClick={() => toggleSort(col.key)}
        data-testid={`sort-${col.key}`}
        title={
          ["three_year", "five_year", "ten_year"].includes(col.key)
            ? "Annualized total return (dividends reinvested)"
            : undefined
        }
      >
        {col.label}
        {sortKey === col.key &&
          (sortDir === "asc" ? (
            <ArrowUp className="size-3" />
          ) : (
            <ArrowDown className="size-3" />
          ))}
      </button>
    </TableHead>
  );

  return (
    <section className="space-y-3">
      {/* The nav already names this page, so the content leads with the data. When a
          compare is armed, a slim banner explains the next tap. */}
      {compareFrom && (
        <div
          className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm"
          data-testid="compare-banner"
        >
          <span className="flex items-center gap-2">
            <ArrowLeftRight className="size-4 text-primary" />
            Comparing <strong>{compareFrom}</strong>. Pick a second security to compare.
          </span>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="size-7"
            aria-label="cancel-compare"
            onClick={() => setCompareFrom(null)}
          >
            <X className="size-4" />
          </Button>
        </div>
      )}

      <Card className="gap-3 py-3">
        <CardHeader className="flex flex-col gap-3 space-y-0 py-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <CardTitle className="text-base">Watchlist</CardTitle>
            <div className="flex min-h-4 flex-wrap items-center gap-1 text-xs text-muted-foreground">
              <span>
                {watchlist.length > 0 && `${watchlist.length} ${watchlist.length === 1 ? "security" : "securities"} · `}
                Prices as of {timeAgo(latestFetch)}
              </span>
              {sessionTag && (
                <span className="rounded bg-warning/15 px-1.5 py-0.5 font-medium text-warning">
                  {sessionTag}
                </span>
              )}
              {trackedSymbol && (
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                  · <Star className="size-3 fill-primary text-primary" /> tracked
                </span>
              )}
              {cooldownRemaining > 0 && (
                <span data-testid="refresh-cooldown">
                  · refresh available in {countdownLabel(cooldownRemaining)}
                </span>
              )}
            </div>
          </div>
          <form
            className="flex w-full items-center gap-2 sm:w-auto"
            onSubmit={(e) => {
              e.preventDefault();
              void addSymbol();
            }}
          >
            <Input
              value={newSymbol}
              onChange={(e) => setNewSymbol(e.target.value)}
              aria-label="add-symbol"
              placeholder="Add symbol"
              className="h-8 flex-1 sm:w-32 sm:flex-none"
            />
            <Button
              type="submit"
              size="sm"
              className="h-8"
              disabled={busy || !newSymbol.trim()}
              data-testid="add-symbol-submit"
            >
              <Plus className="size-4" />
              Add
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              className="size-8 shrink-0"
              aria-label="refresh-watchlist"
              title={
                cooldownRemaining > 0
                  ? `Refresh available in ${countdownLabel(cooldownRemaining)}`
                  : "Recompute prices and risk metrics"
              }
              disabled={refreshing || cooldownRemaining > 0}
              onClick={() => void refresh()}
            >
              <RefreshCw className={"size-4" + (refreshing ? " animate-spin" : "")} />
            </Button>
          </form>
        </CardHeader>
        <CardContent className="pt-0">
          {initialLoad ? (
            <QuoteRowsSkeleton />
          ) : watchlist.length === 0 ? (
            <div className="space-y-4 py-2" data-testid="watchlist-empty">
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  Your watchlist is empty. Start with a few popular funds.
                </p>
                <p className="text-sm text-muted-foreground">
                  Add any of these to track prices, drawdown, and risk, or type your own
                  ticker above. You can remove them anytime.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {STARTER_SYMBOLS.map(({ symbol, label }) => (
                  <button
                    key={symbol}
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm transition-colors hover:border-primary/50 hover:bg-primary/5 disabled:opacity-50"
                    onClick={() => void addStarter(symbol)}
                    disabled={seeding !== null}
                    data-testid={`starter-${symbol}`}
                  >
                    {seeding === symbol ? (
                      <RefreshCw className="size-3.5 animate-spin" />
                    ) : (
                      <Plus className="size-3.5 text-primary" />
                    )}
                    <span className="font-medium">{symbol}</span>
                    <span className="text-xs text-muted-foreground">{label}</span>
                  </button>
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => void addAllStarters()}
                disabled={seeding !== null}
                data-testid="starter-add-all"
              >
                {seeding === "all" ? (
                  <>
                    <RefreshCw className="size-4 animate-spin" />
                    Adding…
                  </>
                ) : (
                  <>
                    <Plus className="size-4" />
                    Add all {STARTER_SYMBOLS.length}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <>
            {/* Mobile: stacked cards (the wide numeric table does not fit a phone), with a
                dropdown to pick the sort since column headers are desktop-only. */}
            <div className="md:hidden">
              <div className="mb-1.5 flex items-center justify-end gap-2 rounded-md bg-muted/30 p-1.5">
                <span className="text-xs text-muted-foreground">Sort by</span>
                <Select
                  value={sortKey}
                  onValueChange={(v) => {
                    setSortKey(v as SortKey);
                    setSortDir(v === "symbol" ? "asc" : "desc");
                  }}
                >
                  <SelectTrigger className="h-8 w-40" aria-label="sort-by">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLUMNS.map((c) => (
                      <SelectItem key={c.key} value={c.key}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="size-8 shrink-0"
                  aria-label="toggle-sort-direction"
                  onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                >
                  {sortDir === "asc" ? (
                    <ArrowUp className="size-4" />
                  ) : (
                    <ArrowDown className="size-4" />
                  )}
                </Button>
              </div>
              <ul className="divide-y divide-border" data-testid="watchlist-mobile">
                {sorted.map((w) => (
                  <li
                    key={w.symbol}
                    className={
                      "py-1.5 transition-colors " +
                      (isTracked(w.symbol) ? "-mx-2 rounded-md bg-primary/5 px-2" : "")
                    }
                    data-testid="watchlist-row-mobile"
                  >
                    <div className="grid grid-cols-[1.5rem_minmax(0,1fr)_auto_auto_auto] items-start gap-2">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-6 text-muted-foreground"
                        onClick={() => toggleExpanded(w.symbol)}
                        aria-expanded={isExpanded(w.symbol)}
                        aria-label={`toggle-risk-mobile-${w.symbol}`}
                      >
                        {isExpanded(w.symbol) ? (
                          <ChevronDown className="size-3.5" />
                        ) : (
                          <ChevronRight className="size-3.5" />
                        )}
                      </Button>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 font-medium">
                          {isTracked(w.symbol) && (
                            <Star
                              className="size-3.5 fill-primary text-primary"
                              aria-label="tracked security"
                            />
                          )}
                          <span>{w.symbol}</span>
                        </div>
                        {w.name && (
                          <div
                            className="truncate text-xs text-muted-foreground"
                            title={w.name}
                          >
                            {shortName(w.name)}
                          </div>
                        )}
                      </div>
                      <div className="min-w-16 text-right tabular-nums">
                        <div className="font-medium">{money(w.latest_price)}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {quoteTime(w.price_fetched_at)}
                        </div>
                      </div>
                      <div
                        className={
                          "min-w-16 text-right tabular-nums " +
                          changeColor(w.today_change_percent)
                        }
                      >
                        <div className="font-medium">{signedMoney(dailyPriceChange(w))}</div>
                        <div className="text-xs">{signedPercent(w.today_change_percent)}</div>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className={
                            "size-6 " +
                            (compareFrom === w.symbol
                              ? "text-primary"
                              : "text-muted-foreground hover:text-primary")
                          }
                          aria-label={`compare-mobile-${w.symbol}`}
                          aria-pressed={compareFrom === w.symbol}
                          onClick={() => pickCompare(w.symbol)}
                        >
                          <ArrowLeftRight className="size-3.5" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="size-6 text-muted-foreground hover:text-destructive"
                          aria-label={`remove-${w.symbol}`}
                          onClick={() => void removeSymbol(w.symbol)}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </div>
                    {isExpanded(w.symbol) && (
                      <div
                        className="mt-1.5 space-y-2 border-t border-border/60 pt-2 pl-8"
                        data-testid="risk-detail-mobile"
                      >
                        {quoteSessionLine(w) && (
                          <div className="truncate text-[10px] text-muted-foreground">
                            {quoteSessionLine(w)}
                          </div>
                        )}
                        <div className="grid grid-cols-3 gap-x-3 gap-y-2 text-xs">
                          <ReturnTag label="DD" value={w.drawdown_percent} />
                          <ReturnTag label="YTD" value={w.returns?.ytd} />
                          <ReturnTag label="1Y" value={w.returns?.one_year} />
                          <ReturnTag label="3Y CAGR" value={cagr(w.returns?.three_year, 3)} />
                          <ReturnTag label="5Y CAGR" value={cagr(w.returns?.five_year, 5)} />
                          <ReturnTag label="10Y CAGR" value={cagr(w.returns?.ten_year, 10)} />
                        </div>
                        <RiskDetail
                          item={w}
                          preferredWindow={riskWindow}
                          onSelectWindow={selectRiskWindow}
                        />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Desktop/tablet: the full sortable table. */}
            <div className="hidden overflow-x-auto md:block">
              <Table className="[&_td:not(:first-child)]:border-l [&_td]:border-border/40 [&_th:not(:first-child)]:border-l [&_th]:border-border/40">
                <TableHeader className="bg-muted/30">
                  <TableRow>
                    {COLUMNS.map((col) => (
                      <SortHeader key={col.key} col={col} />
                    ))}
                    <TableHead className="h-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((w) => (
                    <Fragment key={w.symbol}>
                    <TableRow
                      data-testid="watchlist-row"
                      className={
                        "transition-colors hover:bg-muted/25 " +
                        (isTracked(w.symbol) ? "bg-primary/5" : "")
                      }
                    >
                      <TableCell className="py-1.5">
                        <button
                          type="button"
                          className="flex items-center gap-1.5 text-left font-medium hover:text-primary"
                          onClick={() => toggleExpanded(w.symbol)}
                          aria-expanded={isExpanded(w.symbol)}
                          aria-label={`toggle-risk-${w.symbol}`}
                        >
                          {isExpanded(w.symbol) ? (
                            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground" />
                          )}
                          {isTracked(w.symbol) && (
                            <Star
                              className="size-3.5 fill-primary text-primary"
                              aria-label="tracked security"
                            />
                          )}
                          <span>{w.symbol}</span>
                        </button>
                        {w.name && (
                          <div
                            className="max-w-[16rem] truncate pl-5 text-xs text-muted-foreground"
                            title={w.name}
                          >
                            {shortName(w.name)}
                          </div>
                        )}
                        {quoteSessionLine(w) && (
                          <div className="truncate pl-5 text-[10px] text-muted-foreground">
                            {quoteSessionLine(w)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-1.5 text-right tabular-nums">
                        <div>{money(w.latest_price)}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {quoteTime(w.price_fetched_at)}
                        </div>
                      </TableCell>
                      <TableCell
                        className={"py-1.5 text-right tabular-nums " + changeColor(w.today_change_percent)}
                      >
                        <div>{signedMoney(dailyPriceChange(w))}</div>
                        <div className="text-[10px]">{signedPercent(w.today_change_percent)}</div>
                      </TableCell>
                      <TableCell
                        className={"py-1.5 text-right tabular-nums " + changeColor(w.drawdown_percent)}
                      >
                        {signedPercent(w.drawdown_percent)}
                      </TableCell>
                      <TableCell className={"py-1.5 text-right tabular-nums " + changeColor(w.returns?.ytd)}>
                        {signedPercent(w.returns?.ytd)}
                      </TableCell>
                      <TableCell className={"py-1.5 text-right tabular-nums " + changeColor(w.returns?.one_year)}>
                        {signedPercent(w.returns?.one_year)}
                      </TableCell>
                      <TableCell className={"py-1.5 text-right tabular-nums " + changeColor(cagr(w.returns?.three_year, 3))}>
                        {signedPercent(cagr(w.returns?.three_year, 3))}
                      </TableCell>
                      <TableCell className={"py-1.5 text-right tabular-nums " + changeColor(cagr(w.returns?.five_year, 5))}>
                        {signedPercent(cagr(w.returns?.five_year, 5))}
                      </TableCell>
                      <TableCell
                        className={
                          "py-1.5 text-right tabular-nums " +
                          changeColor(cagr(w.returns?.ten_year, 10))
                        }
                        title="10-year compound annual growth rate"
                      >
                        {signedPercent(cagr(w.returns?.ten_year, 10))}
                      </TableCell>
                      <TableCell className="py-1.5 text-right">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className={
                              "size-7 " +
                              (compareFrom === w.symbol
                                ? "text-primary"
                                : "text-muted-foreground hover:text-primary")
                            }
                            aria-label={`compare-${w.symbol}`}
                            aria-pressed={compareFrom === w.symbol}
                            title={
                              compareFrom === null
                                ? "Compare this security"
                                : compareFrom === w.symbol
                                  ? "Cancel compare selection"
                                  : `Compare ${compareFrom} vs ${w.symbol}`
                            }
                            onClick={() => pickCompare(w.symbol)}
                          >
                            <ArrowLeftRight className="size-4" />
                          </Button>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="size-7 text-muted-foreground hover:text-destructive"
                            aria-label={`remove-${w.symbol}`}
                            onClick={() => void removeSymbol(w.symbol)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded(w.symbol) && (
                      <TableRow data-testid="risk-detail-row">
                        <TableCell colSpan={10} className="bg-muted/30">
                          <RiskDetail
                            item={w}
                            preferredWindow={riskWindow}
                            onSelectWindow={selectRiskWindow}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
            </>
          )}
          {status && <p className="mt-3 text-sm text-destructive">{status}</p>}
        </CardContent>
      </Card>
    </section>
  );
}
